/* ==========================================================================
   Carousel navigation - progress bar, arrows, and optional endless looping
   --------------------------------------------------------------------------
   Mirrors the scroll position of a horizontal rail, either as a short centred
   progress bar or as prev/next arrow buttons. The native scrollbar is not used
   because it always spans the full width of the rail and cannot be shortened,
   centred, or restyled.

   The rail itself stays a real scroll container, so trackpad, touch, and
   keyboard scrolling all still work - this only mirrors and drives it.

   LOOPING. When the section renders data-pc-loop, it has printed the card set
   THREE times and the rail starts parked on the middle copy. As the reader
   scrolls past a copy boundary the scroll position is silently moved back by
   one set width - the pixels under the cursor do not change, so the rail feels
   endless in both directions with no jump to see.

   Three copies rather than two: with two, there is nothing to the left of the
   start, so scrolling backwards hits a hard edge before there is any room to
   wrap. The middle copy gives a full set of runway in each direction.

   Two guards matter. The jump is done with scroll-behavior forced to auto,
   otherwise the browser animates the correction and the reader watches the
   rail slide backwards. And scroll snapping is switched off while looping,
   because a snap target moving under a correction fights it and stutters.

   Looping needs one set to be wider than the rail; with only two or three
   cards it is not, so the loop disables itself and the arrows go back to
   stopping at the ends. Better a rail that stops than one that flickers.

   Filename kept as promo-carousel-bar.js so the existing section reference and
   any cached asset URL stay valid.
   ========================================================================== */

(function () {
  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function toggle(el, hide) {
    if (!el) return;
    if (hide) {
      el.setAttribute('hidden', 'hidden');
    } else {
      el.removeAttribute('hidden');
    }
  }

  function initRail(rail) {
    // Guard against double-binding when a section is re-rendered in the editor.
    if (rail.getAttribute('data-pc-init') === 'true') return;
    rail.setAttribute('data-pc-init', 'true');

    var id = rail.getAttribute('data-pc-carousel');
    var root = rail.closest('.promo_cards_sec') || document;

    var bar = root.querySelector('[data-pc-bar="' + id + '"]');
    var thumb = bar ? bar.querySelector('.pc_bar_thumb') : null;

    var arrows = root.querySelector('[data-pc-arrows="' + id + '"]');
    var prev = arrows ? arrows.querySelector('[data-pc-arrow="prev"]') : null;
    var next = arrows ? arrows.querySelector('[data-pc-arrow="next"]') : null;

    var wantsLoop = rail.getAttribute('data-pc-loop') === 'true';
    var perSet = parseInt(rail.getAttribute('data-pc-set-size'), 10) || 0;

    if (!thumb && !arrows && !wantsLoop) return;

    var minWidth = 10; // never let the bar thumb vanish on very long rails
    var correcting = false;
    var looping = false;

    function cards() {
      return rail.querySelectorAll('.promo_card');
    }

    function scrollable() {
      return rail.scrollWidth - rail.clientWidth;
    }

    // Distance from the first card of one copy to the first card of the next.
    // Measured from the elements rather than derived from scrollWidth, which
    // also carries the rail's own left and right padding.
    function setWidth() {
      if (!perSet) return 0;
      var list = cards();
      if (list.length < perSet * 2) return 0;
      return list[perSet].offsetLeft - list[0].offsetLeft;
    }

    // One card plus one gap, measured live rather than assumed, so the step
    // stays correct when the card width setting or the breakpoint changes.
    function step() {
      var card = rail.querySelector('.promo_card');
      if (!card) return Math.round(rail.clientWidth * 0.8);
      var gap = parseFloat(window.getComputedStyle(rail).columnGap);
      if (isNaN(gap)) gap = 0;
      return Math.round(card.getBoundingClientRect().width + gap);
    }

    function jumpTo(left) {
      correcting = true;
      var previous = rail.style.scrollBehavior;
      rail.style.scrollBehavior = 'auto';
      rail.scrollLeft = left;
      rail.style.scrollBehavior = previous;
      // Released on the next frame: setting scrollLeft fires a scroll event,
      // and re-entering the correction from inside itself would loop.
      window.requestAnimationFrame(function () {
        correcting = false;
      });
    }

    function setUpLoop() {
      var w = setWidth();
      // One copy has to out-measure the viewport, or there is not enough
      // runway either side of the middle copy to correct into.
      var viable = wantsLoop && w > rail.clientWidth;

      if (viable === looping) return;
      looping = viable;

      if (looping) {
        rail.style.scrollSnapType = 'none';
        jumpTo(w);
      } else {
        rail.style.scrollSnapType = '';
      }
    }

    function normalise() {
      if (!looping || correcting) return;
      var w = setWidth();
      if (w <= 0) return;

      // Corrected before the reader reaches a real edge, never at it, so the
      // rail never briefly refuses to move.
      if (rail.scrollLeft >= w * 2) {
        jumpTo(rail.scrollLeft - w);
      } else if (rail.scrollLeft <= w * 0.25) {
        jumpTo(rail.scrollLeft + w);
      }
    }

    function update() {
      var max = scrollable();
      var idle = max <= 1;

      // Nothing to scroll: hide the controls rather than show a full-width
      // thumb or two dead arrows that imply the rail is interactive.
      toggle(bar, idle);
      toggle(arrows, idle);
      if (idle) return;

      // A progress bar cannot describe a rail with no start or end, so it is
      // hidden while looping rather than left showing a meaningless position.
      if (looping) {
        toggle(bar, true);
      } else if (thumb) {
        var ratio = rail.clientWidth / rail.scrollWidth;
        var widthPct = Math.max(ratio * 100, minWidth);
        thumb.style.width = widthPct + '%';
        thumb.style.left = (rail.scrollLeft / max) * (100 - widthPct) + '%';
      }

      // Arrows never disable while looping - there is no end to arrive at.
      if (prev) prev.disabled = looping ? false : rail.scrollLeft <= 1;
      if (next) next.disabled = looping ? false : rail.scrollLeft >= max - 1;
    }

    function go(direction) {
      rail.scrollBy({
        left: direction * step(),
        behavior: reduceMotion() ? 'auto' : 'smooth'
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        go(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        go(1);
      });
    }

    rail.addEventListener(
      'scroll',
      function () {
        normalise();
        update();
      },
      { passive: true }
    );

    function recheck() {
      setUpLoop();
      update();
    }

    window.addEventListener('resize', recheck);

    // Card images load late and change scrollWidth, so recheck once settled.
    window.addEventListener('load', recheck);
    if (window.ResizeObserver) {
      new ResizeObserver(recheck).observe(rail);
    }

    recheck();
  }

  function initAll(scope) {
    var rails = (scope || document).querySelectorAll('[data-pc-carousel]');
    Array.prototype.forEach.call(rails, initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: re-bind when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
